import { useEffect, useRef } from 'react';

import { type SetTimeoutId } from '@pkg/types';
import { randomId } from '@pkg/utils';
import WebGL from '@pkg/webgl';
import { FloorGameWebGLVariant } from '@pkg/webgl/types';

import { getMashedUrl } from '@app/helpers';

import { type CreateLayerOptions, type FloorGameWebGlOptions } from './types';

const ANIMATE_OUT_DELAY = {
    [FloorGameWebGLVariant.TierDown]: 10000,
    [FloorGameWebGLVariant.TierUp]: 15000,
};

export function useFloorGameWebGL({
    domElement,
    name,
    collectionName,
    image,
    mashWidth = 0,
    animateOutDelay = 0,
    borderRadius = 0,
    hasBorder = false,
    isDetailView = false,
    isCollectionView = false,
    onDeactivate,
    onDeleted,
}: FloorGameWebGlOptions) {
    const layerIdRef = useRef(randomId());
    const timeoutRef = useRef<SetTimeoutId>();

    const imageUrl = getMashedUrl({
        width: mashWidth,
        url: image,
    });

    useEffect(() => {
        const cachedId = layerIdRef.current;
        // Delete layer on unmount (if not already deleted).
        return () => WebGL.deleteLayer(cachedId);
    }, []);

    function animateIn() {
        const layer = WebGL.getLayer(layerIdRef.current);

        if (!layer) return;

        setTimeout(() => {
            layer.scene.animateIn();
        }, 500);
        layer.animateIn('fade-in');
    }

    async function animateOut() {
        const layer = WebGL.getLayer(layerIdRef.current);

        if (!layer) return;

        onDeactivate?.();

        await layer.animateOut('fade-out');
        WebGL.deleteLayer(layerIdRef.current);

        onDeleted?.();
    }

    async function createLayer(options: CreateLayerOptions) {
        await WebGL.ready();

        WebGL.addCardLayer(layerIdRef.current, domElement, {
            ...options,
            name,
            collectionName,
            image: imageUrl,
            borderRadius,
            hasBorder,
            isCollectionView,
            isDetailView,
        });

        // Layer Creation is expensive, so delay the "entrance" animation.
        // TODO: This needs to be replaced with a some kind of "onReady"
        // event so that we no longer rely on a magic number.
        setTimeout(() => {
            animateIn();
        }, 30);

        if (options.type === FloorGameWebGLVariant.TierUp || options.type === FloorGameWebGLVariant.TierDown) {
            const delay = animateOutDelay || ANIMATE_OUT_DELAY[options.type];

            timeoutRef.current = setTimeout(() => {
                animateOut();
                timeoutRef.current = undefined;
            }, delay);
        }
    }

    function deactivateFloorGame() {
        WebGL.deleteLayer(layerIdRef.current);
        // TODO: Fix this once our environment understands we are
        // not using a `NodeJs.Timeout`.
        if (typeof timeoutRef.current === 'number') clearTimeout(timeoutRef.current);
    }

    function activateFloorGame(options: CreateLayerOptions) {
        // Delete Layer if new animation is called.
        // Example: Animation Up running & Price goes down.
        if (WebGL.getLayer(layerIdRef.current)) {
            deactivateFloorGame();
        }

        createLayer(options);
    }

    return { activateFloorGame, deactivateFloorGame };
}
